import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { AppSettings } from "app/modules/shared/app.settings";
import { MiFormComponent } from "app/modules/shared/components/mi-form/mi-form.component";
import type { AttributeData } from "app/modules/shared/models/attribute.models";
import { Country } from "app/modules/shared/models/country";
import { Language } from "app/modules/shared/models/language";
import { CommonBindingDataService } from "app/modules/shared/services/common-binding-data.service";
import { ConfigService } from "app/modules/shared/services/config.service";
import { EntityService } from "app/modules/shared/services/entity.service";
import { RestApiService } from "app/modules/shared/services/rest-api.service";
import * as _ from 'lodash';
import { MenuItem, MessageService } from "primeng/api";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TabViewModule } from "primeng/tabview";

interface ProtocolProductAttributeData {
  tabs: Array<{
    tabCode: string;
    tabIndex: number;
    groups: Array<{
      fields: Array<{
        attributeCode: string;
        attributeValue: string;
        isDisable: boolean;
        attributeId?: string;
        disabled?: boolean;
        validation?: { required: boolean };
        presetValues: Array<{
          labelValue: string;
          labelKey: string;
        }>;
      }>;
    }>;
  }>;
  [key: string]: unknown;
}

interface ColumnDefinition {
  attributeCode: string;
  attributeIndex: number;
  field: string;
  header: string;
  [key: string]: unknown;
}

interface AttributeValues {
  [key: string]: unknown;
}

interface RelatedDataItem {
  entityCode: string;
  entityId: string;
  actionStatus: string;
  [key: string]: unknown;
}

interface TabChangeEvent {
  index: number;
  originalEvent: {
    target: {
      innerText: string;
    };
  };
  [key: string]: unknown;
}

interface EntityResponse {
  relatedData: RelatedDataItem[];
  [key: string]: unknown;
}

interface PurchaseOrderData {
  forTenantCode: string;
  entityCode: string;
  countryCode: string;
  languageCode: string;
  data: Array<{
    attributeCode?: string;
    attributeValue: string | string[];
    attributeId?: string;
  }>;
  relationshipData: Array<{
    entityRelationshipConfigId: string;
    otherEntityId: string;
  }>;
}

interface EntitiesData {
  countryCode: string;
  tenantCode: string;
}

@Component({
  selector: 'app-add-purchase-order',
  standalone: true,
  imports: [
    BreadcrumbModule,
    TabViewModule,
    MiFormComponent,
    DialogModule,
    ButtonModule,
    TranslateModule,
    ProgressSpinnerModule
  ],
  templateUrl: './add-purchase-order.component.html',
  styleUrl: './add-purchase-order.component.scss'
})
export class AddPurchaseOrderComponent implements OnInit {
  activeIndex: number = 0;
  data: AttributeData;
  protocolProductAttributeData: ProtocolProductAttributeData;
  originalPPOrderAttributeData: ProtocolProductAttributeData;
  routePath: MenuItem[] = [];
  attributeLabels: Record<string, string> = {};
  moduleName: string;
  country: Country;
  language: Language;
  relation: string = "oneToOne";
  cols: ColumnDefinition[] = [];
  protocolProductAttributeLength: number;
  protocolProductRelation: any;
  attributeValues: AttributeValues = {};
  nextBtnLabel: string;
  previousBtnLabel: string;
  label: string;
  initialLabel: string = this.cs.getLabel("lbl_details");
  saveProtocolProductBtnLabel: string;
  isFirstTabSaved: boolean = false;
  isNew: boolean = true;
  prefixToAttachForButtonIdToUniquelyIdentifyButton = AppSettings.BUTTON_PREFIX
  tabCode: string;
  tabIndex: number;
  header: string;
  @Input() protocolEntityId: string;
  @Input() purchaseOrderId: string;
  @Input() isChangeReasonNotRequired: boolean = false;
  @Input() isEditNotAllowedSpecificStatus: boolean = false;
  @Input() showAddDialog;
  @Output() onToggleOverLay: EventEmitter<boolean> = new EventEmitter();
  protocolAttributeId: string;
  isLoading: boolean = true;
  private documentDetailsMap = new Map<string, any>();

  @ViewChild(MiFormComponent) miFormComponent: MiFormComponent;
  @Output() saveSuccess = new EventEmitter<void>();

  constructor(
    private entityService: EntityService,
    private cs: CommonBindingDataService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private configService: ConfigService,
    private restApiService: RestApiService
  ) { }

  ngOnInit() {
    this.initializeLabels();
    this.initializeEntityService();
    if (this.purchaseOrderId) {
      this.loadProtocolProductData();
    } else {
      this.getAttributes();
    }
    this.setRoutePath('details');
    this.isFirstTabSaved = false;
    this.activeIndex = 0;
  }

  private initializeLabels() {
    this.header = this.purchaseOrderId ?
      this.cs.getLabel("protocol.lbl_edit_purchase") :
      this.cs.getLabel("protocol.lbl_add_purchase");
    this.nextBtnLabel = this.purchaseOrderId ?
      this.cs.getLabel("protocol.lbl_update_purchase_order") :
      this.cs.getLabel("protocol.lbl_add_purchase_order");
    this.previousBtnLabel = this.cs.getLabel("lbl_previous");
    this.saveProtocolProductBtnLabel = this.purchaseOrderId ?
      this.cs.getLabel("protocol.lbl_update_purchase_order") :
      this.cs.getLabel("protocol.lbl_add_purchase_order");
  }

  private initializeEntityService() {
    this.moduleName = AppSettings.ENTITY_CODE.PROTOCOL_PURCHASE_ORDER;
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.entityService.setRelatedData([]);
    this.documentDetailsMap.clear();
  }

  private loadProtocolProductData() {
    this.isLoading = true;
    if (this.purchaseOrderId) {
      this.entityService
        .getEntity(this.purchaseOrderId, AppSettings.ENTITY_CODE.PROTOCOL_PURCHASE_ORDER, AppSettings.VIEW_CODE.ADD_EDIT_VIEW)
        .subscribe({
          next: (res: EntityResponse) => {
            if (res) {
              this.processProtocolProductResponse(res);
              this.isNew = false;
              const currentTab = this.protocolProductAttributeData?.tabs[this.activeIndex];
              if (currentTab) {
                this.setRoutePath(currentTab.tabCode);
              }
            }
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          }
        });
    } else {
      this.getAttributes();
    }
  }

  private toFileIdArray(value: any): string[] {
    if (!value) return [];
    const arr: any[] = Array.isArray(value)
      ? [].concat(...value)
      : [String(value)];
    return arr.filter(Boolean).map(String);
  }

  private processProtocolProductResponse(data: EntityResponse) {
    this.attributeValues = this.cs.getOrgAttributeValues(data) as unknown as AttributeValues;
    this.processRelatedData(data.relatedData);
    const fileIds = this.toFileIdArray(this.attributeValues.upload_document);
    if (fileIds.length > 0) {
      this.fetchFileDetailsBeforeAttributes(fileIds);
    } else {
      this.getAttributes();
    }
  }

  private fetchFileDetailsBeforeAttributes(fileIds: string[]) {
    this.restApiService.post(
      'get purchase order document details for edit',
      `${this.configService.getLoggedInTenantCode()}/api/v1/files/details`,
      { forTenantCode: this.configService.getForTenantCode(), uploadType: AppSettings.DOCUMENTS_TYPE.DOCUMENT, fileIds }
    ).subscribe({
      next: (fileDetails: any) => {
        if (Array.isArray(fileDetails)) {
          fileDetails.forEach(f => {
            if (f.fileId) this.documentDetailsMap.set(f.fileId, f);
            if (f.resourceId) this.documentDetailsMap.set(f.resourceId, f);
          });
        }
        this.getAttributes();
      },
      error: () => this.getAttributes()
    });
  }

  private processRelatedData(relatedData: RelatedDataItem[]) {
    if (relatedData.length > 0) {
      const organizedData: { [key: string]: unknown[] } = {};
      relatedData.forEach(item => {
        if (item.actionStatus === "DELETED") {
          return;
        }
        const entityCode = item.entityCode;
        if (!organizedData[entityCode]) {
          organizedData[entityCode] = [];
        }
        const entityData = this.cs.getOrgAttributeValues(item) as unknown as Record<string, unknown>;
        entityData.entityId = item.entityId;
        organizedData[entityCode].push(entityData);
      });
      this.entityService.setRelatedData(organizedData);
    }
  }

  onTabChange(event: TabChangeEvent) {
    const currentTab = this.protocolProductAttributeData.tabs[this.activeIndex];
    if (currentTab) {
      this.setRoutePath(currentTab.tabCode);
    }
    if (event.index === 0) {
      this.isFirstTabSaved = false;
    }
    if (!this.isFirstTabSaved && event.index > 0) {
      this.activeIndex = 0;
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please save details in first tab before proceeding'
      });
      return;
    }

    this.label = event.originalEvent.target.innerText.trim();
  }

  getAttributes() {
    const entityType = AppSettings.ENTITY_CODE.PROTOCOL_PURCHASE_ORDER;
    const viewCode = AppSettings.VIEW_CODE.ADD_EDIT_VIEW;

    this.entityService.getAttributeDefinition(entityType, viewCode).subscribe({
      next: (res: unknown) => {
        if (!res) {
          this.isLoading = false;
          return;
        }
        this.setProtocolPurchaseOrderView(res);
        const currentTab = this.protocolProductAttributeData.tabs[this.activeIndex];
        if (currentTab) {
          this.setRoutePath(currentTab.tabCode);
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  setProtocolPurchaseOrderView(res: any) {
    this.attributeLabels = this.cs.getAttributeLabels(res) as unknown as Record<string, string>;
    this.originalPPOrderAttributeData = this.cs.getOrganizedAttribute(res) as unknown as ProtocolProductAttributeData;
    this.protocolAttributeId = this.originalPPOrderAttributeData?.tabs[0]?.groups[0]?.fields?.find(
      (element: { attributeCode: string }) => element.attributeCode === 'protocol_entity_id')?.attributeId as string;
    this.protocolProductAttributeData = _.cloneDeep(this.originalPPOrderAttributeData);

    this.protocolProductAttributeData.tabs = this.protocolProductAttributeData.tabs.map(tab => ({
      ...tab,
      groups: tab.groups.map(group => ({
        ...group,
        fields: group.fields.filter((field: { attributeCode: string;[key: string]: unknown }) => {
          if (field.attributeCode === 'purchase_order_number') {
            if (this.isEditNotAllowedSpecificStatus && this.purchaseOrderId) {
              field.disabled = true;
            }
          }

          if (field.attributeCode === 'protocol_entity_id') {
            return false;
          }
          if (field.attributeCode === 'change_reason') {
            if (this.isChangeReasonNotRequired) {
              return false;
            } else {
              if (field.validation) {
                (field.validation as { required: boolean }).required = true;
              }
              return true;
            }
          }

          if (field.attributeCode === 'upload_document') {
            const fileIds = this.toFileIdArray(this.attributeValues.upload_document);
            field.documentURLs = fileIds.map(id => this.documentDetailsMap.get(id)?.signedUrl).filter(Boolean);
            field.documentNames = fileIds.map(id => this.documentDetailsMap.get(id)?.originalFileName || 'Document');
          }
          return true;
        })
      }))
    }));

    this.protocolProductAttributeData.tabs = this.cs.setDependsOnAttributeCode(this.protocolProductAttributeData?.tabs);
    this.protocolProductAttributeLength = this.protocolProductAttributeData.tabs.length;
    this.protocolProductRelation = _.find(res.relations, { otherEntityCode: 'protocol', relation: "oneToOne" });
  }

  onPreviousClick() {
    this.activeIndex = this.activeIndex - 1;
  }

  setRoutePath(tabCode: string) {
    this.routePath = [
      {
        label: this.cs.getLabel("protocol.lbl_protocol"),
        routerLink: '/app/trials',
        icon: 'pi pi-arrow-left',
        iconStyle: { 'font-weight': 'bold', 'margin-right': '10px' }
      },
      {
        label: this.cs.getLabel(tabCode),
        styleClass: 'breadcrumb-child forward-slash breadcrumb-text',
        style: { 'display': 'flex', 'top': '2px', 'position': 'relative' }
      }
    ];
  }

  onSaveProtocolProduct(event: Record<string, unknown>) {
    const purchaseOrderData: PurchaseOrderData = {
      forTenantCode: this.configService.getForTenantCode(),
      entityCode: this.moduleName,
      countryCode: this.country[0].countryCode,
      languageCode: this.language?.[0]?.langCode || 'en',
      data: [],
      relationshipData: []
    };

    const pData = event;
    for (const [key, value] of Object.entries(pData)) {
      if (value) {
        const attributeValue = key === 'upload_document'
          ? this.toFileIdArray(value)
          : value as string;
        if (attributeValue) {
          purchaseOrderData.data.push({ attributeCode: key, attributeValue });
        }
      }
    }

    purchaseOrderData.data.push({
      attributeId: this.protocolAttributeId,
      attributeValue: this.protocolEntityId
    });

    purchaseOrderData.relationshipData?.push({
      entityRelationshipConfigId: this.protocolProductRelation.entityRelationshipConfigId,
      otherEntityId: this.protocolEntityId
    });

    const entitiesData: EntitiesData = {
      countryCode: this.country[0].countryCode,
      tenantCode: this.configService.getForTenantCode(),
    };

    if (!this.purchaseOrderId) {
      this.entityService
        .createEntities(this.moduleName, entitiesData)
        .subscribe((res: any) => {
          this.saveProtocolProductData(purchaseOrderData, res.entityId, true);
        });
    } else {
      this.saveProtocolProductData(purchaseOrderData, this.purchaseOrderId, false);
    }
  }

  saveProtocolProductData(purchaseOrderData: PurchaseOrderData, entityId: string, isNew: boolean) {
    this.isNew = isNew;
    purchaseOrderData.data = this.cs.mapAttributeIds(purchaseOrderData.data, this.originalPPOrderAttributeData.tabs);
    this.entityService
      .saveAttributeData(purchaseOrderData.entityCode, entityId, purchaseOrderData).subscribe({
        next: () => {
          const success = this.cs.getLabelValue('protocol_purchase_order.message.success.create', this.attributeLabels, this.cs.getLabel('protocol.lbl_purchase_order_added_successfully'));
          const update = this.cs.getLabelValue('protocol_purchase_order.message.success.update', this.attributeLabels, this.cs.getLabel('protocol.lbl_purchase_order_updated_successfully'));
          if (this.purchaseOrderId) {
            this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: update });
          } else {
            this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: success });
          }

          setTimeout(() => {
            this.saveSuccess.emit();
          }, 500);
        },
        error: () => {
          if (isNew) {
            this.cs.onDisplayMessage(this.data, 'protocol', AppSettings.MSG_ACTION.ADD, 'error');
          } else {
            this.cs.onDisplayMessage(this.data, 'protocol', AppSettings.MSG_ACTION.UPDATE, 'error');
          }
        }
      });
  }

  onNextBtnClick() {
    if (this.protocolProductAttributeData?.tabs && this.protocolProductAttributeData.tabs.length > 0) {
      this.activeIndex = this.activeIndex + 1;
    }
  }

  onNextClick() {
    if (this.protocolProductAttributeData?.tabs && this.protocolProductAttributeData.tabs.length > 0) {
      this.activeIndex = this.activeIndex + 1;
    }
  }

  onHide() {
    this.showAddDialog = false;
    this.onToggleOverLay.emit(this.showAddDialog);
  }

  onCancel() {
    this.showAddDialog = false;
    this.onToggleOverLay.emit(this.showAddDialog);
  }

  getLastIndex(): number {
    return this.protocolProductAttributeData?.tabs?.length - 1;
  }

  isLastIndex(): boolean {
    return this.activeIndex === this.getLastIndex();
  }
} 